import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../lib/app-context';
import { relative } from '../lib/format';
import { Badge, EmptyState, Spinner } from '../components/ui';
import type { Notification } from '../lib/types';
import { t } from '../lib/i18n';
import { translateNotification } from '../lib/server-text';

/** Notifications, each linking straight to whatever it is about. */

const TARGET: Record<string, (data: Record<string, unknown>) => string | null> = {
  BOOKING_REQUESTED: (data) => (data.bookingId ? `/reservations/${data.bookingId}` : null),
  BOOKING_ACCEPTED: (data) => (data.bookingId ? `/reservations/${data.bookingId}` : null),
  BOOKING_DECLINED: (data) => (data.bookingId ? `/reservations/${data.bookingId}` : null),
  BOOKING_CONFIRMED: (data) => (data.bookingId ? `/reservations/${data.bookingId}` : null),
  BOOKING_CANCELLED: (data) => (data.bookingId ? `/reservations/${data.bookingId}` : null),
  BOOKING_COMPLETED: (data) => (data.bookingId ? `/reservations/${data.bookingId}` : null),
  MESSAGE_RECEIVED: (data) => (data.conversationId ? `/messages/${data.conversationId}` : '/messages'),
  KYC_APPROVED: () => '/verification',
  KYC_REJECTED: () => '/verification',
  LISTING_APPROVED: (data) => (data.propertyId ? `/hote/annonces/${data.propertyId}` : '/hote'),
  LISTING_REJECTED: (data) => (data.propertyId ? `/hote/annonces/${data.propertyId}` : '/hote'),
  COLOCATION_INTEREST: (data) =>
    data.conversationId ? `/messages/${data.conversationId}` : '/messages',
  REVIEW_PUBLISHED: (data) => (data.bookingId ? `/reservations/${data.bookingId}` : '/compte'),
  PAYOUT_PAID: () => '/hote',
  BOOKING_EXPIRED: (data) => (data.bookingId ? `/reservations/${data.bookingId}` : '/compte'),
  PAYMENT_FAILED: (data) => (data.bookingId ? `/reservations/${data.bookingId}` : '/compte'),
  ADMIN_LISTING_PENDING: () => '/admin',
  ADMIN_KYC_PENDING: () => '/admin',
  WELCOME: () => '/recherche',
  LISTING_ALERT: (data) => (data.propertyId ? `/logement/${data.propertyId}` : '/recherche'),
};

export default function Notifications() {
  const { toast, reportError } = useApp();
  const [items, setItems] = useState<Notification[] | null>(null);
  const [unread, setUnread] = useState(0);

  const load = async () => {
    try {
      const response = await api.get<{ items: Notification[]; unread: number }>(
        '/notifications?limit=50',
      );
      setItems(response.items);
      setUnread(response.unread);
    } catch (error) {
      reportError(error);
      setItems([]);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page page--mid stack">
      <div className="row row--between">
        <h1 style={{ fontSize: 24 }}>{t("Notifications")}</h1>
        {unread > 0 ? (
          <button
            className="btn btn--ghost btn--sm"
            onClick={async () => {
              await api.post('/notifications/read', {});
              toast(t("Tout marqué comme lu."), 'ok');
              await load();
            }}
          >
            {t("Tout marquer comme lu")}
          </button>
        ) : null}
      </div>

      {items === null ? (
        <Spinner label={t("Chargement…")} />
      ) : items.length === 0 ? (
        <EmptyState
          title={t("Aucune notification")}
          description={t("Les réponses des hôtes, les confirmations et les messages arrivent ici.")}
        />
      ) : (
        <div className="stack">
          {items.map((notification) => {
            const target = TARGET[notification.type]?.(notification.data) ?? null;
            const content = (
              <div className="card card--pad row row--between row--wrap" style={{ gap: 10 }}>
                <div className="stack stack--tight">
                  <div className="row" style={{ gap: 8 }}>
                    <span className="strong">{translateNotification(notification.title)}</span>
                    {!notification.read ? <Badge tone="badge--verified">{t("Nouveau")}</Badge> : null}
                  </div>
                  {notification.body ? <span className="small muted">{translateNotification(notification.body)}</span> : null}
                </div>
                <span className="tiny muted nowrap">{relative(notification.createdAt)}</span>
              </div>
            );
            return target ? (
              <Link key={notification.id} to={target} style={{ color: 'inherit' }}>
                {content}
              </Link>
            ) : (
              <div key={notification.id}>{content}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}
